/**
 * Member loop-canvas route (OL-193) — eagerly registers
 * `/orca/loops/$loopId/canvas` and re-applies its OWN guard; the page component
 * and its atom / `MermaidGraph` imports are deferred to `canvas.lazy.tsx` so no
 * diagram library lands in the always-loaded chunk (`packages/web/budgets.json`,
 * "Web Bundle Budgets").
 *
 * `orcaloops.ai/loops/<id>/canvas` rewrites onto this path — every orcaloops.ai
 * path does, because `/orca` is a WILDCARD domain prefix — which is why the file
 * lives under `routes/orca/` and not a bare `routes/loops/` the rewrite could
 * never reach.
 *
 * **`requireAuth`, never `requireAdmin`** — the artifacts/collect pattern
 * exactly. `/orca/loops` is an `ORCA_PUBLIC_ROOTS` entry so the `/orca` layout
 * guard steps aside on the ADMIN check; this guard puts a session back in its
 * place. A person reading their own loop's document is not an operator, and
 * `requireAdmin` would bounce the page's only audience to `/access-denied`. The
 * session is necessary but not sufficient: `LoopDefinitionRpcs` resolves the
 * owning customer from the actor and answers `Forbidden` for everybody else.
 *
 * @since 1.0.0
 */
import { createFileRoute } from "@tanstack/react-router";

import { loopViewerStylesheetLinks } from "~/lib/loop-shell-head.ts";
import { requireAuth } from "~/lib/server-auth.ts";

/**
 * The viewer's stylesheets as render-blocking head links (OL-259, widened by
 * OL-318).
 *
 * `LoopFlow.tsx` also side-effect-imports the graph sheet, which keeps the
 * styles with the lazy chunk for dev and for tests — but a sheet the SSR markup
 * depends on has to be a `<link>` in the head the server sends, or the canvas
 * paints raw and then jumps once the chunk lands (`packages/web/CLAUDE.md`,
 * "Performance budgets", rule 6).
 *
 * The page mounts the orcaloops frame, whose sheet is as load-bearing as the
 * graph's — hence the whole viewer set rather than `loop-flow.css` alone. Every one of them arrives through a `?url` import,
 * which is what keeps this file eager-safe: each yields the built sheet's URL,
 * never `@xyflow/react`.
 *
 * @since 1.0.0
 */
export const loopCanvasStylesheetLinks = loopViewerStylesheetLinks;

/**
 * The route's `head()`. Extracted so it is unit-testable without the route
 * machinery, exactly as {@link loopCanvasBeforeLoad} is.
 *
 * @since 1.0.0
 */
export const loopCanvasHead = () => ({ links: loopCanvasStylesheetLinks });

/**
 * The canvas search contract: an optional `?version=<n>` selecting a pinned
 * edition. Absent selects the living head.
 *
 * @since 1.0.0
 */
export interface LoopCanvasSearch {
  readonly version?: number;
}

/**
 * Keep `version` only when it parses to a positive integer; anything else is
 * dropped so a junk `?version` reads as "the head" rather than erroring. Both
 * the number form (already-parsed search) and the string form (raw query) are
 * accepted.
 *
 * @since 1.0.0
 */
export const validateLoopCanvasSearch = (search: Record<string, unknown>): LoopCanvasSearch => {
  const raw = search["version"];
  const value = typeof raw === "number" ? raw : typeof raw === "string" ? Number(raw) : Number.NaN;
  return Number.isInteger(value) && value >= 1 ? { version: value } : {};
};

/**
 * The guard, extracted so it is unit-testable without the route machinery.
 *
 * @since 1.0.0
 */
// oxlint-disable-next-line eslint-js/no-restricted-syntax -- TanStack redirects require async/await
export const loopCanvasBeforeLoad = async () => {
  // oxlint-disable-next-line eslint-js/no-restricted-syntax -- TanStack redirects require async/await
  const auth = await requireAuth();
  return { auth };
};

export const Route = createFileRoute("/orca/loops/$loopId/canvas")({
  validateSearch: validateLoopCanvasSearch,
  beforeLoad: loopCanvasBeforeLoad,
  head: loopCanvasHead,
});
